// MyOrgDetail — donor portal per-org detail page (CHG-1517 wave 2).
//
// /my-giving/org/:orgId. Consumes:
//   GET /api/v1/my-giving/org/{org_id}            primary (PortalOrgDetail)
//   GET /api/v1/my-giving/org/{org_id}/updates    secondary (broadcast feed)
//
// Hero card with org name + logo + mission. KPI strip with totals
// (donated, donation count, active recurring, open pledges). Updates
// list at the bottom.

import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/composition/PageHeader";
import { useFormatters } from "../contexts/IntlContext";
import {
  meOrgDetailApi,
  type PortalOrgDetail,
  type PortalOrgUpdate,
} from "../api/me-org-detail";

function KpiCard({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div
      style={{
        padding: 16,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: 8,
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 500,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 600,
          color: "var(--color-text)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Hero({ org }: { org: PortalOrgDetail }) {
  return (
    <section
      style={{
        display: "flex",
        gap: 16,
        alignItems: "center",
        padding: 16,
        background: "var(--surface-2, #f6f7fb)",
        borderRadius: 8,
        marginBottom: 16,
      }}
    >
      {org.logo_url ? (
        <img
          src={org.logo_url}
          alt=""
          loading="lazy"
          style={{
            width: 64,
            height: 64,
            borderRadius: 8,
            objectFit: "cover",
            background: "var(--color-border)",
          }}
        />
      ) : (
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 8,
            background: "var(--color-border)",
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{org.name}</h2>
        {org.mission_statement ? (
          <p
            style={{
              fontSize: 13,
              color: "var(--color-text-muted)",
              margin: "6px 0 0",
              lineHeight: 1.4,
            }}
          >
            {org.mission_statement}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function UpdatesList({ items }: { items: PortalOrgUpdate[] }) {
  if (items.length === 0) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: "center",
          color: "var(--color-text-muted)",
          fontSize: 13,
        }}
      >
        No recent updates from this organization.
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((u) => (
        <article
          key={u.id}
          style={{
            padding: 12,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
          }}
        >
          {u.title ? (
            <h3
              style={{
                fontSize: 14,
                fontWeight: 600,
                margin: "0 0 6px",
                color: "var(--color-text)",
              }}
            >
              {u.title}
            </h3>
          ) : null}
          <p
            style={{
              fontSize: 13,
              color: "var(--color-text)",
              margin: "0 0 6px",
              lineHeight: 1.4,
            }}
          >
            {u.body}
          </p>
          <div
            style={{
              fontSize: 11,
              color: "var(--color-text-muted)",
            }}
          >
            {/* CHG-2947 (DP-R16) — author_name was a phantom field the
                runtime never emits; render only the (nullable)
                published date from the real PortalFeedItem shape. */}
            {u.published_at
              ? new Date(u.published_at).toLocaleDateString()
              : ""}
          </div>
        </article>
      ))}
    </div>
  );
}

export function MyOrgDetail() {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const { formatCurrency } = useFormatters();
  const orgQuery = useQuery({
    queryKey: ["me", "org", orgId] as const,
    queryFn: () => meOrgDetailApi.fetchMyOrgDetail(orgId),
    enabled: !!orgId,
  });
  const updatesQuery = useQuery({
    queryKey: ["me", "org", orgId, "updates"] as const,
    queryFn: () => meOrgDetailApi.fetchMyOrgUpdates(orgId),
    enabled: !!orgId,
    staleTime: 30_000,
  });

  return (
    <main
      style={{
        maxWidth: 1280,
        margin: "0 auto",
        padding: "16px 24px 48px",
      }}
    >
      <PageHeader
        title={orgQuery.data?.name ?? "Organization"}
        subtitle={
          <Link
            to="/my-giving"
            style={{ color: "var(--accent, #0066cc)", fontSize: 13 }}
          >
            ← Back to My Giving
          </Link>
        }
      />
      {orgQuery.isLoading ? (
        <div
          style={{ padding: 32, color: "var(--color-text-muted)", fontSize: 14 }}
        >
          Loading organization…
        </div>
      ) : orgQuery.error || !orgQuery.data ? (
        <div style={{ padding: 32, textAlign: "center" }}>
          <p style={{ fontSize: 14, color: "var(--color-text-muted)" }}>
            Couldn't load this organization. It may not exist or you may not
            have given to it.
          </p>
        </div>
      ) : (
        <>
          <Hero org={orgQuery.data} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <KpiCard
              label="You've donated"
              value={formatCurrency(
                orgQuery.data.total_donated_minor_units,
                orgQuery.data.currency,
              )}
            />
            <KpiCard
              label="Donations"
              value={String(orgQuery.data.donation_count)}
            />
            <KpiCard
              label="Active recurring"
              value={String(orgQuery.data.active_recurring_count)}
            />
            <KpiCard
              label="Open pledges"
              value={String(orgQuery.data.open_pledges_count)}
            />
          </div>
          <section aria-labelledby="org-updates-title">
            <h2
              id="org-updates-title"
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--color-text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                margin: "0 0 12px",
              }}
            >
              Recent updates
            </h2>
            {updatesQuery.isLoading ? (
              <div
                style={{
                  padding: 16,
                  color: "var(--color-text-muted)",
                  fontSize: 13,
                }}
              >
                Loading updates…
              </div>
            ) : (
              <UpdatesList items={updatesQuery.data ?? []} />
            )}
          </section>
        </>
      )}
    </main>
  );
}
